import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import useBreakpoint from "../../hooks/use-breakpoint";

import AccessoriesBanner from "./components/AccessoriesBanner";
import AccessoriesSearchBar from "./components/AccessoriesSearchBar";
import AccessoryCard from "./components/AccessoryCard";
import AccessoryEmptyState from "./components/AccessoryEmptyState";
import AccessoryFiltersPanel from "./components/AccessoryFiltersPanel";
import ResultsBar from "./components/ResultsBar";
import SectionHeader from "./components/SectionHeader";
import {
  AccessoryGridSkeleton,
  CategoryChipsSkeleton,
} from "./components/AccessorySkeletons";
import {
  CategoryChips,
  LocationPrompt,
} from "./components/AccessoriesToolbar";

import useAccessoryFeed from "./useAccessoryFeed";
import useAccessoryTaxonomy from "./useAccessoryTaxonomy";
import useDebouncedTerm from "./useDebouncedTerm";
import usePriceBounds from "./usePriceBounds";
import useSkeletonGate from "./useSkeletonGate";

import { emptyState, unavailableSummary } from "./accessoryCopy";
import {
  INITIAL_FILTERS,
  countActiveFilters,
  hasCoordinates,
  normaliseFilters,
  normaliseSort,
  sortLabel,
} from "./accessoryFilters";
import { ALL_CATEGORY_KEY } from "./accessoryMappers";
import { accessoryPath } from "./accessoryRoutes";
import { readUrlState, writeUrlState } from "./accessoryUrlState";

import useMapLocation from "../search/useMapLocation";

import "./styles.css";

const AccessoriesRoute = () => {
  const navigate = useNavigate();
  const breakpoint = useBreakpoint();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialRef = useRef(readUrlState(searchParams));

  const [categoryCode, setCategoryCode] = useState(
    initialRef.current.categoryCode
  );
  const [filters, setFilters] = useState(initialRef.current.filters);
  const [term, setTerm] = useState(initialRef.current.term);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const location = useMapLocation({});
  const coordinates = location.coordinates;
  const located = hasCoordinates(coordinates);

  const taxonomy = useAccessoryTaxonomy();
  const search = useDebouncedTerm(term);

  const effectiveFilters = useMemo(
    () => ({
      ...normaliseFilters(filters),
      sort: normaliseSort(filters.sort, located),
    }),
    [filters, located]
  );

  const feed = useAccessoryFeed({
    containerOptionId: taxonomy.containerOptionId,
    categoryCode,
    term: search.term,
    filters: effectiveFilters,
    coordinates,
    enabled: taxonomy.isReady,
  });

  const bounds = usePriceBounds({
    params: feed.params,
    enabled: taxonomy.isReady,
  });

  const urlState = useMemo(
    () => ({ categoryCode, term, filters: effectiveFilters }),
    [categoryCode, term, effectiveFilters]
  );

  const replaceRef = useRef(true);

  useEffect(() => {
    const next = writeUrlState(urlState);

    if (next.toString() === searchParams.toString()) return;

    setSearchParams(next, { replace: replaceRef.current });
    replaceRef.current = true;
  }, [urlState, searchParams, setSearchParams]);

  const commit = useCallback((updater) => {
    replaceRef.current = false;
    updater();
  }, []);

  const [resetSignal, setResetSignal] = useState(0);

  useEffect(() => {
    if (resetSignal === 0) return;

    feed.refetch();
  }, [resetSignal, feed.refetch]);

  const selectCategory = (chip) => commit(() => setCategoryCode(chip.code));

  const applyFilters = (values) =>
    commit(() => setFilters(normaliseFilters(values)));

  const resetFilters = () =>
    commit(() => {
      setFilters(INITIAL_FILTERS);
      setResetSignal((value) => value + 1);
    });

  const clearAll = () =>
    commit(() => {
      setFilters(INITIAL_FILTERS);
      setTerm("");
      setCategoryCode(null);
      setResetSignal((value) => value + 1);
    });

  const openAccessory = (card) =>
    navigate(accessoryPath({ productId: card.id, coordinates }));

  const activeCount = countActiveFilters(effectiveFilters);

  const requerying =
    feed.isRequerying || (search.pending && feed.items.length > 0);
  const showSkeleton = useSkeletonGate(requerying) || feed.isFirstLoad;

  const selectedChip = taxonomy.chips.find(
    (chip) => (chip.code ?? null) === (categoryCode ?? null)
  );

  const unavailable = useMemo(
    () => unavailableSummary(feed.items),
    [feed.items]
  );

  const empty = feed.isEmpty
    ? emptyState({
        term: search.term,
        categoryLabel:
          selectedChip && selectedChip.key !== ALL_CATEGORY_KEY
            ? selectedChip.label
            : null,
        activeFilters: activeCount,
      })
    : null;

  const failed = taxonomy.isError || feed.isError;

  const narrowed = Boolean(activeCount || categoryCode || search.term);

  const sectionTitle =
    selectedChip && selectedChip.key !== ALL_CATEGORY_KEY
      ? selectedChip.label
      : "All Accessories";

  if (failed) {
    return (
      <div className="acc-screen">
        <AccessoryEmptyState
          state={emptyState({ isError: true })}
          onRetry={() => {
            taxonomy.refetch();
            feed.refetch();
          }}
        />
      </div>
    );
  }

  return (
    <div className="acc-screen">
      <header className="acc-hero">
        <AccessoriesBanner />

        <AccessoriesSearchBar
          term={term}
          onTermChange={setTerm}
          sort={effectiveFilters.sort}
          onSortChange={(sort) => applyFilters({ ...effectiveFilters, sort })}
          located={located}
          onSubmit={feed.refetch}
          activeCount={activeCount}
          onOpenFilters={() => setFiltersOpen(true)}
        />
      </header>

      <div className="acc-band">
        <div className="acc-tabs">
          {taxonomy.isResolving ? (
            <CategoryChipsSkeleton />
          ) : (
            <CategoryChips
              chips={taxonomy.chips}
              selected={selectedChip?.key ?? ALL_CATEGORY_KEY}
              onSelect={selectCategory}
            />
          )}
        </div>

        {!located && location.canPrompt && (
          <LocationPrompt
            locating={location.locating}
            onLocate={location.locate}
          />
        )}

        <SectionHeader title={sectionTitle}>
          {(narrowed || feed.total > 0) && (
            <ResultsBar
              total={feed.total}
              loading={showSkeleton}
              activeCount={activeCount}
              sortLabel={sortLabel(effectiveFilters.sort)}
              onClear={resetFilters}
            />
          )}
        </SectionHeader>

        {unavailable && (
          <p className="acc-unavailable" role="status">
            {unavailable}
          </p>
        )}

        {showSkeleton ? (
          <AccessoryGridSkeleton />
        ) : empty ? (
          <AccessoryEmptyState
            state={empty}
            canClear={activeCount > 0}
            onClear={clearAll}
          />
        ) : (
          <ul className="acc-grid">
            {feed.items.map((card) => (
              <AccessoryCard
                key={`${card.id}-${card.supplierId}`}
                card={card}
                onOpen={openAccessory}
              />
            ))}
          </ul>
        )}

        {feed.hasMore && !showSkeleton && (
          <div className="acc-more">
            <button
              type="button"
              className="acc-more-button"
              disabled={feed.isLoadingMore}
              onClick={feed.loadMore}
            >
              {feed.isLoadingMore ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>

      <AccessoryFiltersPanel
        open={filtersOpen}
        filters={effectiveFilters}
        materials={taxonomy.materials}
        bounds={bounds.bounds}
        located={located}
        asSheet={breakpoint.width < 900}
        onClose={() => setFiltersOpen(false)}
        onApply={applyFilters}
        onReset={resetFilters}
      />
    </div>
  );
};

export default AccessoriesRoute;
